import React, { useState } from 'react';
import { X, HelpCircle, Users, Check, Network } from 'lucide-react';
import { RESPONSE_MODES } from '../AvatarCardsPanel';

interface AskQuestionAvatar {
  name: string;
  role: string;
}

interface AskQuestionModalProps {
  avatars: AskQuestionAvatar[];
  defaultResponseMode: string;
  onSubmit: (
    question: string,
    avatarRoles: string[],
    maxWords: number | undefined,
    responseMode: string,
    skipGraph: boolean
  ) => void;
  onClose: () => void;
}

export function AskQuestionModal({ avatars, defaultResponseMode, onSubmit, onClose }: AskQuestionModalProps) {
  const [question, setQuestion] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [maxWords, setMaxWords] = useState('');
  const [responseMode, setResponseMode] = useState(defaultResponseMode);
  const [useGraphKnowledge, setUseGraphKnowledge] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const canSubmit = question.trim().length > 0 && selectedRoles.length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const parsedMaxWords = maxWords.trim() ? parseInt(maxWords, 10) : undefined;
    onSubmit(question.trim(), selectedRoles, parsedMaxWords, responseMode, !useGraphKnowledge);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
              <HelpCircle size={16} />
            </div>
            <h2 className="text-base font-bold text-navy">Ask a Question</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Question</label>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              rows={4}
              autoFocus
              placeholder="What would you like to ask the avatar(s)?"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Users size={11} />Send to
            </label>
            <div className="flex flex-col gap-2">
              {avatars.map(avatar => {
                const selected = selectedRoles.includes(avatar.role);
                return (
                  <button
                    key={avatar.role}
                    type="button"
                    onClick={() => toggleRole(avatar.role)}
                    className={`flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                      selected
                        ? 'border-primary bg-primary/5'
                        : 'border-[#e2e8f0] hover:border-primary/40 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`text-sm font-semibold ${selected ? 'text-navy' : 'text-slate-700'}`}>{avatar.name}</span>
                    {selected && (
                      <div className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0">
                        <Check size={12} />
                      </div>
                    )}
                  </button>
                );
              })}
              {avatars.length === 0 && (
                <p className="text-xs text-slate-400">No avatars available in this session.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Max Words</label>
              <input
                type="number"
                min={1}
                value={maxWords}
                onChange={e => setMaxWords(e.target.value)}
                placeholder="Session default"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
              <p className="mt-2 text-xs text-slate-400">Overrides the session default for this request only.</p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Response Mode</label>
              <select
                value={responseMode}
                onChange={e => setResponseMode(e.target.value)}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
              >
                {RESPONSE_MODES.map(mode => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl border border-[#e2e8f0]">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                <Network size={13} />
              </div>
              <div>
                <p className="text-sm font-semibold text-navy">Use Graph Knowledge</p>
                <p className="text-xs text-slate-400">Lets avatars pull project context from the knowledge graph for this question.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setUseGraphKnowledge(v => !v)}
              className={`relative flex-shrink-0 w-10 h-6 rounded-full transition-colors ${useGraphKnowledge ? 'bg-primary' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${useGraphKnowledge ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
          >
            {submitting ? 'Sending…' : 'Send Question'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AskQuestionModal;
